import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import Actions from './Actions';

const SensitiveWordsSection = () => {
  const { t } = useTranslation();

  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const words = await api.get<Array<{ word: string }>>(
        '/backoffice/sensitive-words?source=platform',
      );
      setContent(words.map((w) => w.word).join('、'));
      setDirty(false);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);

    try {
      await api.put('/backoffice/sensitive-words/platform', { content });
      setDirty(false);
      toast.success(t('common.save_success'));
    } catch (err: any) {
      toast.error(err.message || t('common.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-5">
          <AlertCircle className="w-5 h-5 text-foreground" />
          <h3 className="text-base font-medium text-foreground">
            {t('config.sensitive_words')}
          </h3>
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-semibold text-foreground">
            {t('config.sensitive_words_list')}
          </label>
          <Textarea
            rows={3}
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              setDirty(true);
            }}
            placeholder={t('config.sensitive_words_placeholder')}
          />
          <p className="text-sm text-muted-foreground">
            {t('config.sensitive_words_hint')}
          </p>
        </div>

        <Actions
          onCancel={load}
          onSave={handleSave}
          saving={saving}
          disabled={!dirty}
        />
      </CardContent>
    </Card>
  );
};

export default SensitiveWordsSection;
